// App.js
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavigationContainer } from '@react-navigation/native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { Ionicons } from '@expo/vector-icons';
import { StatusBar } from 'expo-status-bar';
import { Platform, Text, TouchableOpacity, View, StyleSheet, Image } from 'react-native';
import { useFonts, Inter_300Light, Inter_400Regular, Inter_500Medium, Inter_600SemiBold, Inter_700Bold } from '@expo-google-fonts/inter';
import AsyncStorage from '@react-native-async-storage/async-storage';
import MixerScreen from './src/screens/MixerScreen';
import LibraryScreen from './src/screens/LibraryScreen';
import StudyScreen from './src/screens/StudyScreen';
import KidsScreen from './src/screens/KidsScreen';
import PreviewScreen from './src/screens/PreviewScreen';
import RoadmapScreen from './src/screens/RoadmapScreen';
import HelpScreen from './src/screens/HelpScreen';
import OnboardingScreen from './src/screens/OnboardingScreen';
import { ONBOARDING_KEY } from './src/utils/onboarding';
import { LibraryProvider } from './src/context/LibraryContext';
import { JukeboxProvider } from './src/context/JukeboxContext';
import { AudioEngine } from './src/audio/AudioEngine';
import { MediaSessionManager } from './src/audio/MediaSessionManager';
import BatteryOptimizationPrompt from './src/components/BatteryOptimizationPrompt';
import VersionModal from './src/components/VersionModal';
import { Colors } from './src/theme';

// AsyncStorage key — set to 'true' once the user has seen the battery prompt.
const BATTERY_PROMPT_KEY = 'battery_opt_prompt_shown';

const Tab = createBottomTabNavigator();

const styles = StyleSheet.create({
  splash: {
    flex: 1,
    backgroundColor: Colors.bg,
  },
  appRoot: {
    flex: 1,
  },
});

const headerBtnStyles = StyleSheet.create({
  btn: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  label: {
    color: Colors.accent,
    fontSize: 10,
    fontFamily: 'Inter_600SemiBold',
  },
  disabled: {
    opacity: 0.45,
  },
});

// Shared info button rendered in every tab's header right.
// onPress → replay intro; long-press → open VersionModal.
function InfoButton({ onPress, onLongPress, disabled = false }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      onLongPress={onLongPress}
      disabled={disabled}
      delayLongPress={600}
      hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
      accessibilityLabel="App info"
      accessibilityHint="Tap to replay intro. Long-press to see version info."
      accessibilityState={{ disabled }}
      style={[headerBtnStyles.btn, disabled && headerBtnStyles.disabled]}
    >
      <Ionicons name="information-circle-outline" size={20} color={Colors.accent} />
      <Text style={headerBtnStyles.label} maxFontSizeMultiplier={1.5} numberOfLines={1}>Info</Text>
    </TouchableOpacity>
  );
}


// Shown only on the web build — the Android app is live on Google Play, while
// visitors reaching sleepmixblueprint.com in a browser are viewing the web
// version rather than the full native app experience.
function WebComingSoonBanner() {
  if (Platform.OS !== 'web') return null;
  return (
    <View style={webBannerStyles.bar}>
      <View style={webBannerStyles.content}>
        <Text style={webBannerStyles.text} numberOfLines={2}>
          🌙 The SleepMixBlueprint app is live on Google Play for Android — not on the Apple App Store just yet. You're viewing the web version. Get the full Android app below.
        </Text>
        <a
          href="https://play.google.com/store/apps/details?id=com.sleepmix.app"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Get it on Google Play"
          style={webBannerStyles.badgeLink}
        >
          <Image
            source={require('./assets/google-play-badge.png')}
            style={webBannerStyles.badge}
            resizeMode="contain"
            accessibilityLabel="Get it on Google Play"
          />
        </a>
      </View>
    </View>
  );
}

const webBannerStyles = StyleSheet.create({
  bar: {
    backgroundColor: Colors.surface2,
    borderBottomColor: Colors.accent + '33',
    borderBottomWidth: 1,
    paddingVertical: 8,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  content: {
    width: '100%',
    maxWidth: 900,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  text: {
    color: Colors.accent,
    fontSize: 12,
    fontFamily: 'Inter_600SemiBold',
    textAlign: 'center',
    maxWidth: 480,
  },
  badgeLink: {
    display: 'flex',
    flexShrink: 0,
  },
  badge: {
    width: 200,
    height: 77,
  },
});const TAB_ICONS = {
  Sleep:      { default: 'moon-outline',          active: 'moon' },
  'My Music': { default: 'musical-notes-outline', active: 'musical-notes' },
  Study:      { default: 'book-outline',          active: 'book' },
  Kids:       { default: 'happy-outline',         active: 'happy' },
  Preview:    { default: 'phone-portrait-outline', active: 'phone-portrait' },
  Roadmap:    { default: 'map-outline',           active: 'map' },
  Help:       { default: 'help-circle-outline',   active: 'help-circle' },
};

function App() {
  const [fontsLoaded, fontError] = useFonts({
    Inter_300Light,
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
  });

  // Log font errors for telemetry but don't block the app — fall through to
  // system fonts so the user isn't stuck on the loading screen forever.
  useEffect(() => {
    if (fontError) {
      console.warn('[App] Font load error (falling back to system fonts):', fontError);
    }
  }, [fontError]);

  // null = still checking, false = show onboarding, true = skip onboarding
  const [onboardingSeen, setOnboardingSeen] = useState(null);
  // Manual intro replay is transient and must not replace the running app tree.
  const [showIntroOverlay, setShowIntroOverlay] = useState(false);

  // Battery-optimisation prompt — Android only, shown at most once per install.
  const [showBatteryPrompt, setShowBatteryPrompt] = useState(false);
  // 'reactive' = triggered automatically after audio stopped; 'manual' = user tapped the header button.
  const [batteryPromptMode, setBatteryPromptMode] = useState('reactive');
  // Version / OTA info modal — triggered by long-pressing the ℹ️ button.
  const [showVersionModal, setShowVersionModal] = useState(false);
  // Guard: set to true when a long-press fires so the subsequent onPress
  // callback (which React Native delivers on finger-up) is swallowed.
  const infoLongPressed = useRef(false);
  // Guard against firing the prompt multiple times in the same session.
  const batteryPromptTriggered = useRef(false);

  useEffect(() => {
    AudioEngine.init().then(() => {
      // Start syncing the lock-screen / Control Center media card with the
      // active mix. MediaSessionManager subscribes to AudioEngine state changes
      // and keeps the OS Now Playing entry up to date.
      MediaSessionManager.init();
    });
    AsyncStorage.getItem(ONBOARDING_KEY)
      .then((val) => setOnboardingSeen(val === 'true'))
      .catch(() => setOnboardingSeen(false));
  }, []);

  // Wire up the battery-optimisation prompt. We only do this on Android
  // because the OEM battery-killer problem is Android-specific. The callback
  // fires from AudioEngine when it detects audio stopping unexpectedly while
  // the app is in the background — the hallmark of an OEM battery-saver kill.
  useEffect(() => {
    if (Platform.OS !== 'android') return;

    const unsubscribe = AudioEngine.onUnexpectedBackgroundStop(async () => {
      // Prevent repeat prompts within the same session.
      if (batteryPromptTriggered.current) return;
      batteryPromptTriggered.current = true;

      // Only show the prompt once per install across sessions.
      try {
        const alreadyShown = await AsyncStorage.getItem(BATTERY_PROMPT_KEY);
        if (alreadyShown === 'true') return;
        await AsyncStorage.setItem(BATTERY_PROMPT_KEY, 'true');
      } catch (_) {
        // AsyncStorage failure is non-fatal; still show the prompt this session.
      }

      setBatteryPromptMode('reactive');
      setShowBatteryPrompt(true);
    });

    return unsubscribe;
  }, []);

  const showIntroAgain = useCallback(() => {
    setShowIntroOverlay(true);
  }, []);

  // Manual re-open: bypasses the one-time AsyncStorage gate so the user can
  // revisit the battery settings prompt whenever they want (Android only).
  const showBatterySettingsManually = useCallback(() => {
    setBatteryPromptMode('manual');
    setShowBatteryPrompt(true);
  }, []);

  // Wait until we know whether to show onboarding AND fonts are ready —
  // showing the app background avoids a black flash on cold start, and
  // holding until fontsLoaded prevents a flash of unstyled (system-font) text.
  if (onboardingSeen === null || (!fontsLoaded && !fontError)) {
    return <View style={styles.splash} />;
  }

  if (!onboardingSeen) {
    return (
      <>
        <StatusBar style="light" />
        <OnboardingScreen onDone={() => setOnboardingSeen(true)} />
      </>
    );
  }

  return (
    <JukeboxProvider>
    <LibraryProvider>
      <View style={styles.appRoot}>
        <StatusBar style="light" />
        <WebComingSoonBanner />
        <BatteryOptimizationPrompt
          visible={showBatteryPrompt}
          onDismiss={() => setShowBatteryPrompt(false)}
          mode={batteryPromptMode}
        />
        <VersionModal
          visible={showVersionModal}
          onClose={() => setShowVersionModal(false)}
        />
        <NavigationContainer>
        <Tab.Navigator
          screenOptions={({ route }) => ({
            headerShown: true,
            // Small brand icon shown in the header of every tab except Sleep
            // (Sleep tab shows the full logo inside the scroll content instead).
            headerLeft: route.name === 'Sleep' ? undefined : () => (
              <Image
                source={require('./assets/sleepmix_icon.png')}
                style={{ width: 28, height: 28, borderRadius: 6, marginLeft: 14, opacity: 0.9 }}
                resizeMode="contain"
                accessibilityLabel="SleepMixBlueprint"
              />
            ),
            // Default info button shown on every tab. The Sleep tab overrides
            // headerRight to also include the Battery button.
            headerRight: () => (
              <View style={{ marginRight: 16 }}>
                <InfoButton
                  disabled={showIntroOverlay}
                  onPress={() => {
                    if (infoLongPressed.current) {
                      infoLongPressed.current = false;
                      return;
                    }
                    showIntroAgain();
                  }}
                  onLongPress={() => {
                    infoLongPressed.current = true;
                    setShowVersionModal(true);
                  }}
                />
              </View>
            ),
            headerStyle: {
              backgroundColor: Colors.bg,
              borderBottomColor: Colors.border,
              borderBottomWidth: 1,
              elevation: 2,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 1 },
              shadowOpacity: 0.3,
              shadowRadius: 4,
            },
            headerTitleStyle: {
              color: Colors.textBright,
              fontSize: 17,
              fontFamily: fontsLoaded ? 'Inter_600SemiBold' : undefined,
            },
            headerTintColor: Colors.accent,

            tabBarStyle: {
              backgroundColor: Colors.bg,
              borderTopColor: Colors.accent + '33',
              borderTopWidth: 1,
              paddingBottom: 6,
              height: 60,
            },
            tabBarActiveTintColor: Colors.accent,
            tabBarInactiveTintColor: Colors.textMuted,
            tabBarLabelStyle: {
              fontSize: 11,
              fontFamily: fontsLoaded ? 'Inter_600SemiBold' : undefined,
              marginBottom: 2,
            },

            tabBarIcon: ({ color, size, focused }) => {
              const icon = focused
                ? TAB_ICONS[route.name].active
                : TAB_ICONS[route.name].default;
              return <Ionicons name={icon} size={size} color={color} />;
            },
          })}
        >
          <Tab.Screen
            name="Sleep"
            component={MixerScreen}
            options={{
              title: 'Sleep',
              headerTitle: 'SleepMixBlueprint',
              headerRight: () => (
                <View style={{ flexDirection: 'row', alignItems: 'center', marginRight: 16, gap: 16 }}>
                  {Platform.OS === 'android' && (
                    <TouchableOpacity
                      onPress={showBatterySettingsManually}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                      accessibilityLabel="Fix background audio"
                      accessibilityHint="Opens battery optimisation settings"
                      style={headerBtnStyles.btn}
                    >
                      <Ionicons name="battery-charging-outline" size={20} color={Colors.accent} />
                      <Text style={headerBtnStyles.label} maxFontSizeMultiplier={1.5}>Battery</Text>
                    </TouchableOpacity>
                  )}
                  <InfoButton
                    disabled={showIntroOverlay}
                    onPress={() => {
                      if (infoLongPressed.current) {
                        infoLongPressed.current = false;
                        return;
                      }
                      showIntroAgain();
                    }}
                    onLongPress={() => {
                      infoLongPressed.current = true;
                      setShowVersionModal(true);
                    }}
                  />
                </View>
              ),
            }}
          />
          <Tab.Screen
            name="My Music"
            component={LibraryScreen}
            options={{ headerTitle: 'My Music' }}
          />
          <Tab.Screen
            name="Study"
            component={StudyScreen}
            options={{ headerTitle: 'Study Session' }}
          />
          <Tab.Screen
            name="Kids"
            component={KidsScreen}
            options={{ headerTitle: 'Kids Sleep' }}
          />
          <Tab.Screen
            name="Preview"
            component={PreviewScreen}
            options={{ headerTitle: 'About the Preview' }}
          />
          <Tab.Screen
            name="Roadmap"
            component={RoadmapScreen}
            options={{ headerTitle: "What's Coming" }}
          />
          <Tab.Screen
            name="Help"
            component={HelpScreen}
            options={{ headerTitle: 'Help' }}
          />
        </Tab.Navigator>
        </NavigationContainer>
        {showIntroOverlay && (
          <OnboardingScreen onClose={() => setShowIntroOverlay(false)} />
        )}
      </View>
    </LibraryProvider>
    </JukeboxProvider>
  );
}

export default App;
